import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { logout, verifyToken } from "network/ApiAxios";
import { publishAuthLogout, subscribeToAuthLogout } from "core/authEvents";
import { ROUTES } from "core/links";

const AuthContext = createContext(null);

// Both session shapes are built in more than one place below, so factor them
// out rather than repeat the object literal at each call site.
const authenticatedSession = (user) => ({ status: "authenticated", user, sessionExpired: false });
const anonymousSession = (sessionExpired = false) => ({ status: "anonymous", user: null, sessionExpired });

export const AuthProvider = ({ children }) => {
    const [session, setSession] = useState({ status: "checking", user: null, sessionExpired: false });

    const verifySession = useCallback(async () => {
        try {
            const response = await verifyToken();
            if (response?.success) {
                setSession(authenticatedSession(response.user));
                return;
            }
        } catch (error) {
        }
        // Not a real "session expired" event - this is either the very first
        // bootstrap check (no prior session to have expired) or a re-check
        // that found the same still-anonymous state, so don't touch whatever
        // sessionExpired flag a genuine logout event may have already set.
        setSession((prev) => anonymousSession(prev.sessionExpired));
    }, []);

    useEffect(() => {
        // A 401 on an authenticated request (ApiAxios.js) broadcasts this -
        // the user WAS signed in and got kicked out mid-session, which is
        // worth telling them about, unlike the silent anonymous states above.
        const unsubscribe = subscribeToAuthLogout((reason) => {
            setSession(anonymousSession(reason !== "manual"));
        });
        verifySession();
        return unsubscribe;
    }, [verifySession]);

    const signIn = useCallback((user) => {
        setSession(authenticatedSession(user));
    }, []);

    const signOut = useCallback(async () => {
        await logout();
        setSession(anonymousSession());
        publishAuthLogout("manual");
    }, []);

    const value = useMemo(() => ({
        ...session,
        signIn,
        signOut,
    }), [session, signIn, signOut]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return context;
};

const LOGIN_REDIRECT_SECONDS = 10;

export const LOGIN_REDIRECT_MESSAGE = `Redirecting to login in ${LOGIN_REDIRECT_SECONDS} seconds...`;

// Sends the user to the login page after a short delay once `active` is true.
export const useRedirectToLogin = (active) => {
    const navigate = useNavigate();

    useEffect(() => {
        if (!active) return undefined;
        const timer = setTimeout(() => navigate(ROUTES.LOGIN), LOGIN_REDIRECT_SECONDS * 1000);
        return () => clearTimeout(timer);
    }, [active, navigate]);
};
